import { RD_BASE as AI_BASE } from './config';
import { rdSkill } from './rootdata';
import { detectStage, STAGE_WEIGHTS, calcComposite, fmt } from '../utils/scoring';
import { validateAnalysisV2, summarizeViolations } from '../schemas/analysis';

// ── AI 评分 JSON 鲁棒修复辅助函数（scoreProject 用，模块级导出方便单测）──

// DeepSeek 常见的"漏逗号"问题（如 `"a"\n  "b"` 之间缺 ,），用 lookahead 在
// 字符串/对象/数组 字面量后插逗号，回避字符串内部
export const insertMissingCommas = (s) => s
  // "..."\n  "..."  → "...",\n  "..."
  .replace(/("(?:[^"\\]|\\.)*")(\s*\r?\n\s*)(?=["{[])/g, '$1,$2')
  // }\n  "..." / }\n  { → },\n  ...
  .replace(/(\})(\s*\r?\n\s*)(?=["{[])/g, '$1,$2')
  // ]\n  "..." / ]\n  [ → ],\n  ...
  .replace(/(\])(\s*\r?\n\s*)(?=["{[])/g, '$1,$2');

// 修复字符串值内部未转义的引号。真实事故（Sentient 2026-07-05）：DeepSeek 在中文正文里
// 用直引号强调术语，如 `"recommendation": "建议观望，等待"锁仓期"结束后再入场"`，
// 内嵌的 " 让 JSON.parse 在真正字符串结束前提前截断（"Expected ',' or '}' after
// property value"）。逐字符扫描，遇到 " 时向后看第一个非空白字符：是 , } ] : 或
// 字符串末尾才判定为真正收尾，否则视为内容里的引号，转义后继续。
export function escapeStrayQuotes(s) {
  let out = '';
  let inStr = false, esc = false;
  for (let i = 0; i < s.length; i++) {
    const c = s[i];
    if (esc) { out += c; esc = false; continue; }
    if (c === '\\' && inStr) { out += c; esc = true; continue; }
    if (c === '"') {
      if (!inStr) { inStr = true; out += c; continue; }
      let j = i + 1;
      while (j < s.length && /\s/.test(s[j])) j++;
      const next = s[j];
      if (next === undefined || ',}]:'.includes(next)) { inStr = false; out += c; continue; }
      out += '\\"';
      continue;
    }
    out += c;
  }
  return out;
}

// ── 轻量模式：用 Claude 生成项目基本信息（RootData 搜不到时的兜底）────
export async function buildLightweightDetails(projectName) {
  const prompt = `你是 Web3 行业研究员。项目名："${projectName}"。
请基于你的知识库，返回该项目的基本信息。**仅在你确认知道时才填写 URL/日期；不知道返回空字符串**，不要编造。
严格只返回JSON：
{
  "project_name": "${projectName}",
  "one_liner": "一句话描述项目定位（20字内）",
  "description": "项目详细描述（100-200字，包括解决什么问题、核心产品、目标用户）",
  "tags": ["赛道标签1", "赛道标签2"],
  "ecosystem": ["所属生态链，如Ethereum/Solana/多链"],
  "token_symbol": "代币符号（如有），无则空字符串",
  "establishment_date": "项目成立日期（格式 YYYY 或 YYYY-MM，只在你了解时填写），不知道返回空字符串",
  "first_announcement_date": "项目首次公开发布日期（格式 YYYY-MM），不知道返回空字符串",
  "funding_history": "已知融资历史，格式 'YYYY-MM 轮次 金额'，多笔以 ; 分隔，不知道返回空字符串",
  "social_media": {
    "website": "官网URL（如确认知道），无则空字符串",
    "X": "Twitter/X URL（如确认知道），无则空字符串",
    "github": "GitHub URL（如确认知道），无则空字符串",
    "docs": "文档URL（如确认知道），无则空字符串"
  },
  "confidence": "high|medium|low（你对这些信息准确度的自评）"
}`;

  const res = await fetch(`${AI_BASE}/api/ai/score`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      model: "claude-opus-4-7",
      max_tokens: 500,
      messages: [{ role: "user", content: prompt }],
    }),
  }).catch(() => null);

  if (!res || !res.ok) {
    // Claude 也失败时，返回最基础的 shell
    return {
      project_name: projectName,
      one_liner: "",
      description: "",
      tags: [],
      ecosystem: [],
      token_symbol: "",
      social_media: {},
      _lightweight: true,
      _confidence: "none",
    };
  }

  const ai = await res.json();
  const txt = (ai.content || []).map(b => b.type === "text" ? b.text : "").join("")
    .replace(/```json\s*/g, "").replace(/```\s*/g, "").trim();
  const m = txt.match(/\{[\s\S]*\}/);
  const fallback = {
    project_name: projectName, one_liner: "", description: "",
    tags: [], ecosystem: [], token_symbol: "", social_media: {},
    _lightweight: true, _confidence: "none",
  };
  if (!m) return fallback;

  try {
    // 清理常见 JSON 问题：控制字符、尾逗号
    const cleaned = m[0]
      .replace(/[\x00-\x1f]/g, " ")
      .replace(/,\s*([}\]])/g, "$1");
    const parsed = JSON.parse(cleaned);
    return {
      ...parsed,
      project_name: projectName,
      _lightweight: true,
      _confidence: parsed.confidence || "medium",
    };
  } catch {
    // JSON 解析失败，尝试提取关键字段
    const oneLiner = (txt.match(/"one_liner"\s*:\s*"([^"]*)"/) || [])[1] || "";
    const desc = (txt.match(/"description"\s*:\s*"([^"]*)"/) || [])[1] || "";
    return { ...fallback, one_liner: oneLiner, description: desc };
  }
}

// ── 技术产品深度分析（Claude API 服务端，无 CORS 限制）────────────
export async function fetchTechIntel(details) {
  const website  = details.social_media?.website || "";
  const gitbook  = details.social_media?.gitbook  || "";
  const sm       = details.social_media || {};
  const docsUrl  = gitbook || sm.docs || sm.whitepaper || "";
  const domain   = website.replace(/https?:\/\//, "").replace(/\/.*/, "").replace(/^www\./, "");

  const prompt = `你是 Web3 技术分析师。请基于以下信息分析该项目的技术产品情况和网站流量：

项目名: ${details.project_name}
官网: ${website}
域名: ${domain}
文档: ${docsUrl || "无"}
描述: ${(details.description || "").slice(0, 600)}
one_liner: ${details.one_liner || ""}
赛道: ${(details.tags || []).join(", ")}
生态: ${(details.ecosystem || []).slice(0, 4).join(", ") || "未知"}

请基于你的知识库（包括对该项目官网/文档/GitHub/白皮书的了解），严格只返回JSON：
{
  "product_features": ["核心功能1","核心功能2","核心功能3"],
  "product_features_en": ["Core feature 1","Core feature 2","Core feature 3"],
  "tech_stack": ["技术栈1","技术栈2"],
  "product_stage": "白皮书阶段|测试网|主网上线|产品已落地|企业级产品",
  "product_stage_en": "Whitepaper|Testnet|Mainnet live|Product launched|Enterprise product",
  "differentiators": ["技术差异化1","技术差异化2"],
  "differentiators_en": ["Tech differentiator 1 in English","Differentiator 2"],
  "github_activity": "活跃(有公开代码)|一般|无公开仓库|未知",
  "github_activity_en": "Active (public repos)|Moderate|No public repos|Unknown",
  "competitor_comparison": "与竞品技术对比10字",
  "competitor_comparison_en": "Tech comparison vs competitors, 10 words in English",
  "tech_score_basis": "技术评分核心依据20字",
  "tech_score_basis_en": "Tech score basis in English, ~20 words",
  "data_evidence": ["可量化产品数据点1（网站流量/活跃用户/TPS/产品性能;不要猜测 TVL/Fees, 这些来自 DefiLlama 实时数据;若无可靠数据返回空数组[]）","数据点2"],
  "data_evidence_en": ["Quantifiable product data point 1 (web traffic / DAU / TPS / product perf; DO NOT guess TVL/Fees - those come from DefiLlama; return [] if no reliable data)","Point 2"],
  "tech_risks": ["技术风险1","技术风险2"],
  "tech_risks_en": ["Tech risk 1 in English","Risk 2"],
  "whitepaper_summary": "基于你对该项目白皮书/技术文档的了解，用80-120字概括文档核心内容。如果没有信息返回空字符串。",
  "whitepaper_summary_en": "Based on your knowledge of this project's whitepaper/docs, summarize in 80-120 words in English. Return empty string if unknown.",
  "traffic": {
    "monthly_visits_estimate": "如 500K/mo 或 <10K/mo",
    "traffic_tier": "头部(1000万+)|中型(100-1000万)|小型(10-100万)|早期(<10万)|无数据",
    "traffic_tier_en": "Top (10M+)|Mid (1-10M)|Small (100K-1M)|Early (<100K)|No data",
    "primary_audience": "主要用户群体10字",
    "primary_audience_en": "Primary audience ~10 words in English",
    "top_regions": ["地区1","地区2","地区3"],
    "growth_trend": "快速增长|稳定|下降|未知",
    "growth_trend_en": "Rapid growth|Stable|Declining|Unknown",
    "traffic_basis": "判断依据15字",
    "traffic_basis_en": "Basis for estimate in English, ~15 words"
  }
}`;

  const res = await fetch(`${AI_BASE}/api/ai/score`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      model: "claude-opus-4-7",
      // 之前 800 对这个 ~20 字段 + 双语数组 + traffic 对象的 schema 偏紧，verbose 输出
      // 会在 max_tokens 处截断（stop_reason:"max_tokens"），JSON 未闭合直接抛错，
      // 被上层 .catch(() => null) 静默吞掉 → techIntel 整块数据丢失且无任何提示
      // （2026-07-05 Sentient 分析实测复现：output_tokens=800 命中上限）。
      max_tokens: 1400,
      messages: [{ role: "user", content: prompt }],
    }),
  }).catch(() => null);

  if (!res || !res.ok) return null;
  const ai = await res.json();
  const txt = (ai.content || []).map(b => b.type === "text" ? b.text : "").join("")
    .replace(/```json\s*/g, "").replace(/```\s*/g, "").trim();
  const m = txt.match(/\{[\s\S]*\}/);
  if (!m) return null;
  // 与 scoreProject 共用同一套鲁棒修复，避免偶发格式问题（缺逗号/内嵌引号）
  // 也静默丢数据——之前这里连 try/catch 都没有，任何小瑕疵直接抛到 .catch(() => null)
  const repairAttempts = [
    () => JSON.parse(m[0]),
    () => JSON.parse(m[0].replace(/,\s*([}\]])/g, '$1')),
    () => JSON.parse(insertMissingCommas(m[0])),
    () => JSON.parse(escapeStrayQuotes(m[0])),
    () => JSON.parse(insertMissingCommas(escapeStrayQuotes(m[0])).replace(/,\s*([}\]])/g, '$1')),
  ];
  for (const attempt of repairAttempts) {
    try { return attempt(); } catch { /* try next */ }
  }
  return null;
}

/**
 * fetchWebIntel — 创始人情报 + 产品关键词 + Claude 技术深度分析
 * @param {object} details       - RootData get_item 返回的项目详情
 * @param {Array}  prefetchedHits - 可选：ser_inv 已搜到的 type=3 人物列表（避免重复搜索）
 */
export async function fetchWebIntel(details, prefetchedHits = null) {
  const intel = {
    founders: [],
    founderTwitters: [],
    whitepaperSummary: "",
    websiteTraffic: null,
    websiteKeywords: [],
    docsUrl: "",
  };

  try {
    // 1. 创始人/团队 — 优先用外部传入的 hits（已过滤 type=3），避免重复 ser_inv 请求
    let peopleItems;
    if (prefetchedHits !== null) {
      // 外部已提供（可能为空数组，但不需要再搜一遍）
      peopleItems = prefetchedHits.slice(0, 4);
    } else {
      const searchRes = await rdSkill("ser_inv", {
        query: details.project_name,
        precise_x_search: false,
      }).catch(() => []);
      peopleItems = (searchRes || []).filter(x => x.type === 3).slice(0, 4);
    }

    if (peopleItems.length > 0) {
      intel.founders = peopleItems.map(p => ({
        name: p.name || "",
        introduce: (p.introduce || "").slice(0, 600),
        twitter: p.X || p.twitter || "",
        rootdataurl: p.rootdataurl || "",
        logo: p.logo || "",
      }));
      intel.founderTwitters = intel.founders
        .filter(f => f.twitter)
        .map(f => ({
          handle: f.twitter.split("/").filter(Boolean).pop() || "",
          url: f.twitter,
          name: f.name,
        }));
    }

    // 2. 兜底：从描述中提取 Twitter
    if (intel.founderTwitters.length === 0) {
      const desc = (details.description || "") + " " + (details.one_liner || "");
      const projectHandle = (details.social_media?.twitter || "")
        .split("/").filter(Boolean).pop()?.toLowerCase() || "";
      const twitterMatches = [...desc.matchAll(/(?:twitter|x)\.com\/([A-Za-z0-9_]{3,30})/gi)];
      intel.founderTwitters = [...new Set(twitterMatches.map(m => m[1]))]
        .filter(t => t.toLowerCase() !== projectHandle)
        .slice(0, 4)
        .map(t => ({ handle: t, url: `https://twitter.com/${t}` }));
    }

    // 3. 产品关键词
    if (details.one_liner)   intel.websiteKeywords.push(details.one_liner);
    if (details.description) intel.websiteKeywords.push(details.description.slice(0, 300));
    if (details.ecosystem?.length) {
      intel.websiteKeywords.push("生态：" + details.ecosystem.slice(0, 3).join(", "));
    }

    // 4. Docs 链接
    const sm = details.social_media || {};
    intel.docsUrl = sm.gitbook || sm.docs || sm.whitepaper || "";

    // ── 5. 调用 Claude API 做技术产品深度分析（服务端执行，无 CORS）──
    intel.techIntel = await fetchTechIntel(details).catch(() => null);

    // 6. 白皮书摘要：从 techIntel 的 whitepaper_summary 字段提取
    if (intel.techIntel?.whitepaper_summary) {
      intel.whitepaperSummary = intel.techIntel.whitepaper_summary;
    }

  } catch { /* 静默 */ }

  return intel;
}

// ── AI 六维评分 ──────────────────────────────────────
// data-views: prompt 块（每个 view 一份）
import { buildPromptBlock as buildValuationPromptBlock } from '../data-views/promptBlocks/valuationBlock';
import { buildPromptBlock as buildUnlockPromptBlock    } from '../data-views/promptBlocks/unlockBlock';
import { buildPromptBlock as buildProtocolPromptBlock  } from '../data-views/promptBlocks/protocolBlock';
import { buildPromptBlock as buildHoldersPromptBlock   } from '../data-views/promptBlocks/holdersBlock';
import { buildPromptBlock as buildSocialPromptBlock    } from '../data-views/promptBlocks/socialBlock';
import { buildPromptBlock as buildNewsPromptBlock      } from '../data-views/promptBlocks/newsBlock';

// ── 静态评分规则块（跨所有项目/所有请求字节完全一致）──────────────────
// 2026-07-28 为接入 Anthropic prompt caching 从 scoreProject 内联模板中拆出。
// 原来这段规则+schema 混在按项目变化的数据后面，导致每次请求前缀都不一样、
// 缓存永远不命中。现在把它整体挪到 prompt 最前面，配合调用处的
// cache_control 断点：只要短时间内（1h TTL）有任意用户触发任意项目的分析，
// 都能复用同一份缓存，这部分 input token 只按 ~10% 计费。
// 唯一要求：内容必须 100% 静态，不能有任何按项目变化的插值——原来嵌在这段
// 规则文字中间的几处判断（FDV/TVL 分档、合规查询失败、团队/GitHub/估值信息
// 质量惩罚）已经挪到 scoreProject 内部 quantSignalsBlock，通过 prompt 里的
// 指示性文字引用，不再在这里内联计算。
const SCORE_STATIC_RULES = `你是Web3顶级一级市场投资分析师。以下是从RootData Skill API获取的项目数据，请根据下方评分规则和输出格式进行专业评分。

【评分基准规则 - 必须严格遵守】

■ 叙事与愿景(narrative)
赛道热度基础分（2025-2026 当前周期）：
- AI Agent/DePIN/AI Infra：基础75
- RWA（真实资产上链）：基础72
- Restaking/AVS/再质押：基础70
- L1/L2（有差异化）：基础68
- DeFi 借贷/DEX（成熟赛道）：基础65，需有差异化才能上70+
- GameFi/NFT：基础55（叙事衰退期）
- 元宇宙/P2E：基础45（叙事周期已过）
- 其他/未知赛道：基础60

差异化加分（可叠加）：
- 解决行业真实痛点且有可验证场景：+5~10
- 叙事与当前监管/政策方向高度契合：+5
- 已有知名协议/机构公开合作背书：+5
- 顶级叙事+清晰执行路径：可达85-95

■ 技术与产品(tech)
- Pre-Seed/天使阶段：无产品数据属正常，65分起步
- Seed 阶段：有任何技术数据或MVP：70分起步
- Series A及以上：有主网/产品落地：75分起步，数据出色再往上
- 无公开GitHub仓库且无可验证产品：tech 上限65（信息不透明）

■ 团队背景(team)
- 有相关行业专业背景：70分起步
- 知名机构出身/连续创业者：80-85
- 以下情况给90+：
  · 顶级VC直接领投（a16z/Paradigm/Dragonfly/Binance Labs）的核心团队
  · 前BATJ/蚂蚁/阿里/腾讯/字节核心技术负责人（CTO/VP级别），与Web3直接相关的
  · 前顶级传统金融机构核心团队（高盛/黑石/JP Morgan董事总经理级+）
  · 已有成功加密项目退出记录的连续创业者
- 已标注"顶级VC领投=是"：团队评分自动+5（不超过95）
- 无团队公开信息（无RootData成员+无WebIntel创始人信息）：这是数据覆盖缺口，不是团队本身的负面证据——
  早期、还未被主流关注到的真实项目大概率会命中这一条，不能等同于"团队可疑"。按阶段中性默认给分
  （Pre-Seed/Seed 阶段 65 分左右，不做惩罚性下调），且 notes.team 必须写"团队信息未公开，暂无法验证
  背景"这类中性表述，不得写成"团队可疑""信息不透明"等听起来像负面结论的评语。仅当合规/新闻信号里
  出现主动隐藏身份叠加其他风险信号（如疑似 rug pull 迹象）时，才在 compliance 或 risks 中反映，
  team 维度本身不因单纯"信息缺失"二次扣分。

■ 代币经济(tokenomics)
未发币按阶段默认分：
- Pre-Seed/天使：默认65分
- Seed：默认72分
- Series A及以上：默认75分

已发币量化规则（严格执行，不得因"叙事好"调高）：下方"项目专属量化信号"部分会给出 FDV/TVL、FDV/交易量、Protocol Economics P/S 等已计算好的比率和对应分档判断——按那里给出的具体数字和分档执行，不要自行反推或忽略。
- 解锁结构未披露：额外 -5 分（信息不透明惩罚）
- 通胀率 > 年增发 20%：额外 -5~10 分

■ 数据表现(data)
无数据时按阶段默认分：
- Pre-Seed/天使（无数据属正常）：默认65分
- Seed（少量数据）：默认70分
- Series A及以上无数据：默认72分

有数据时量化评分：
- DeFiLlama 已收录 + TVL > $10M + 周涨幅为正：基础80
- DeFiLlama 已收录 + TVL $1M-$10M：基础72
- DeFiLlama 已收录但 TVL < $1M 或持续下降：基础55~65
- 仅有 CoinGecko 交易量无 TVL：基础60
- 链上数据持续下降（TVL/交易量周环比 -20% 以上）：-10 分

■ 合规风险(compliance) ← 重要：按赛道区分基础分
- 博彩/彩票类（如链上彩票、赌博协议）：最低35分，有牌照才给50+
- 预测市场/衍生品/杠杆交易：最低45分，美国合规做好给65+
- 支付/稳定币/RWA：基础55分，需关注各地牌照
- 常规DeFi/Infra/Layer：基础60分，无明确问题给65-80
- 有主动合规动作（持牌/法律意见书/多辖区合规）：+10~15
- 特别加分项：若合规设计本身构成竞争壁垒（如原生zkKYC/zkDID、获得香港/新加坡/欧盟等监管机构正式认可），合规分可突破80分达到85-90，并在summary中标注"合规即护城河"
下方"项目专属量化信号"部分会说明合规数据库查询是否失败，失败则按上方规则扣分。

【信息完整度处理 - 自动执行，须区分"数据缺口"与"负面证据"，不得因"项目有潜力"豁免真实风险】
下方"项目专属量化信号"部分会给出团队信息缺失/无GitHub仓库/无估值这三项判断：team 按中性默认给分
（不是惩罚性上限，理由见上方团队背景规则）；tech/tokenomics 仍按那里给出的上限执行——因为"能否验证
产品""能否定价"本身就是这两个维度要评估的内容，跟团队维度纯粹的"信息覆盖不足"性质不同，不构成矛盾。

整体评分取向：早期项目给合理成长空间；但赛道固有合规风险不因阶段而豁免。量化指标不得因主观印象调高。

【fair_value_usd 估值规则 - 必须严格遵守】

▶ 估值基准（按优先级）
1. 代币已上线且CoinGecko FDV可信（FDV > 总融资额）→ fair_value = FDV × 1.0~1.5
2. 融资轮次估值已披露 → 用披露估值作为锚点
3. 估值未披露但有融资金额 → 用"估值参考"字段中的推算值作为锚点（按25%稀释推算）
4. 完全无估值信息 → 参考同赛道同阶段中位数给保守估计

▶ 按赛道×阶段套用可比倍数（相对锚点估值）
- Layer1/Layer2：Pre-Seed×10~20 / Seed×8~15 / Series A×4~8
- DeFi/DEX：Pre-Seed×6~12 / Seed×5~10 / Series A×3~6
- Infra/中间件：Pre-Seed×8~15 / Seed×6~12 / Series A×3~6
- AI+Crypto：Pre-Seed×10~25 / Seed×8~20 / Series A×4~10
- RWA/支付/私信贷：Pre-Seed×3~6 / Seed×4~8 / Series A×2~5（TradFi溢价有限）
- GameFi/彩票/博彩：Pre-Seed×2~4 / Seed×3~6（流动性折价+合规折价）
- DePIN：Pre-Seed×6~12 / Seed×5~10

▶ 质量调整
- 顶级VC领投（已在数据中标注）：倍数上限上浮20%
- 无名机构/信息不足：取区间下限
- 已有主网/真实贷款/产品落地：倍数中位数
- 纯白皮书阶段：倍数下限

▶ 改进4：公司与代币分离的项目
- 若项目是运营公司（如信贷公司、交易所、基础设施服务商），本身不直接发币
- 则 fair_value_usd 估算的是公司股权价值，不应直接等同于某代币FDV
- 此类项目的 tge_status 应标注"公司股权，无TGE计划"

▶ 硬性上限
- fair_value 不得超过锚点估值的 12 倍
- 代币已上线不得超过 FDV 的 1.5 倍
- 严禁给出导致低估倍数超过 15x 的值

在 valuation_basis 字段中说明：用了哪个锚点、套用哪个倍数、为什么。

【recommendation 入场价反幻觉规则 — 必须严格遵守】

▶ 适用：recommendation 字段如果提到具体入场价 / 建仓 FDV 区间 / "回调至 $X" 等数字

▶ 触发条件检测（按顺序）：
  1. fair_value_usd 已经算出（不为 null）
  2. recommendation 中出现「建议回调至」「等到」「建仓区间」+ 美元数字
  3. 比较入场价区间和 fair_value：
     - 若区间下限 < fair_value × 0.3 → 你正在建议「跌 70%+ 后买」
     - 若区间上限 < fair_value × 0.5 → 你正在建议「跌 50%+ 后买」

▶ 这两种情况你必须满足以下之一才能输出：
  A. 有明确依据：FDV/Rev 或 P/S 倍率来自 ground truth 数据（DefiLlama 实测 fees / Surf revenue）
  B. 在 recommendation 中显式标注「**基于赛道历史中位数估算**」或「**需进一步验证**」
  C. 直接拒绝给具体价位：用「等待 30% 以上回调」「分批建仓」这类相对表述

▶ 禁止行为：
  ❌ Revenue 缺失（feeDaily=undefined）时凭空算出 FDV/Rev 倍率
  ❌ 用「FDV/Rev XX.Yx 过热」做依据但不交代 Rev 数字哪来的
  ❌ 给出与 fair_value 严重背离的入场价但不解释为什么 fair_value 不准

▶ 案例反面教材（Hyperliquid 2026-06-24）：
  fair_value=$59.4B + recommendation="建议回调至 $500M-1B FDV 建仓" →
  这是 99% 折扣，自相矛盾，被检测后会被 shape 标记 _entry_price_suspicious。

正确写法示例：
  ✓「当前估值合理偏高，FDV/TVL=10x（DefiLlama 实测 TVL $5.8B），建议分批建仓」
  ✓「Rev 数据缺失无法精确定价，建议等 30%+ 回调或解锁数据公开后再评估」
  ✗「FDV/Rev 80.9x 过热，等回调至 $500M-1B」← 80.9x 无依据

【主张级引用规则】
在 note_citations 中，每个维度提供 1-3 个支撑该维度评语的关键事实，格式：
- src: 数据来源（RootData/CoinGecko/DeFiLlama/GitHub/Dune/AI 之一）
- fact: 具体可量化事实，15字以内（如"Seed $2M融资，a16z领投"、"TVL $85M 7日+12%"、"主网上线6个月"）
只引用可核实的数据点，AI推断必须标注src为"AI"。

严格只返回JSON，无其他内容（字符串值内禁止换行符，用空格替代）:
{
  "track": "Layer1|Layer2|DeFi|RWA|AI|GameFi|DePIN|Infra|NFT|其他",
  "track_en": "Same as track but in English (e.g. Other, DeFi Lending, L2 ZK, etc.)",
  "stage": "融资阶段描述",
  "stage_en": "Stage in English (e.g. Seed, Series A, NASDAQ-listed, Pre-Seed, etc.)",
  "tge_status": "已上线|预计XXXX-QX|未定",
  "tge_status_en": "TGE status in English (e.g. Live, Expected 2025-Q3, TBD, Equity company - no TGE planned)",
  "fair_value_usd": 估算合理估值数字(美元),
  "valuation_basis": "估值依据一句话",
  "valuation_basis_en": "Valuation basis in English, one sentence",
  "risk_penalty": 风险折扣(0.0到0.5),
  "scores": {"narrative":0,"tech":0,"team":0,"tokenomics":0,"data":0,"compliance":0},
  "notes": {"narrative":"20字评语","tech":"20字","team":"20字","tokenomics":"估值依据15字+评语","data":"20字","compliance":"20字"},
  "summary": "投资建议80字",
  "unlock_pressure": "Green|Yellow|Red",
  "unlock_schedule": [],
  "catalysts_paired": [
    {"zh":"催化剂1：未来3-6月可能引爆估值的具体事件，区别于现有优势","en":"Catalyst 1: specific upcoming event in 3-6mo that could re-rate valuation"},
    {"zh":"催化剂2","en":"Catalyst 2"},
    {"zh":"催化剂3","en":"Catalyst 3"}
  ],
  "strengths_paired": [
    {"zh":"现有优势1","en":"Existing strength 1"},
    {"zh":"优势2","en":"Strength 2"},
    {"zh":"优势3","en":"Strength 3"}
  ],
  "risks_paired": [
    {"zh":"风险1","en":"Risk 1"},
    {"zh":"风险2","en":"Risk 2"},
    {"zh":"风险3","en":"Risk 3"}
  ],
  "fdv_tiers": [
    {"fdv_range":"$200-500M","rating":"S","position":"建议仓位","position_en":"Suggested position size","thesis":"入场逻辑15字","thesis_en":"Entry thesis in English","expected_return":"10-20x"},
    {"fdv_range":"$500M-1B","rating":"A","position":"建议仓位","position_en":"Suggested position size","thesis":"入场逻辑15字","thesis_en":"Entry thesis in English","expected_return":"4-8x"},
    {"fdv_range":"$1-2B","rating":"B","position":"建议仓位","position_en":"Suggested position size","thesis":"入场逻辑15字","thesis_en":"Entry thesis in English","expected_return":"2-4x"}
  ],
  "comparable_projects": [
    {"name":"Comp name","fdv":"$XXB","stage":"已上线/Pre-TGE","stage_en":"Live/Pre-TGE","similarity":"相似赛道/技术","similarity_en":"Similar track/tech in English","vs":"溢价/折价 如：溢价30%","vs_en":"Premium/Discount e.g. 30% premium"},
    {"name":"Comp name","fdv":"$XXB","stage":"已上线/Pre-TGE","stage_en":"Live/Pre-TGE","similarity":"相似赛道/技术","similarity_en":"Similar in English","vs":"溢价/折价","vs_en":"Premium/Discount"}
  ],
  "exit_paths": ["退出路径1：如TGE后登陆头部CEX","退出路径2：如协议费用分红Staking","退出路径3：如战略并购"],
  "exit_paths_en": ["Exit 1: e.g. TGE + Tier-1 CEX listing","Exit 2: e.g. Protocol fee revenue staking","Exit 3: e.g. Strategic acquisition"],
  "moat": "核心护城河一句话，如：蚂蚁数科独家战略合作+物理资产锚定",
  "recommendation": "综合建议30字：是否值得投资、仓位大小、入场时机关键条件",
  "summary_en": "Investment thesis in English (~80 words)",
  "moat_en": "Core moat in English, one sentence",
  "recommendation_en": "Investment recommendation in English, ~30 words",
  "notes_en": {"narrative":"20-word EN comment","tech":"20-word EN comment","team":"20-word EN comment","tokenomics":"EN comment","data":"20-word EN comment","compliance":"20-word EN comment"},
  "note_citations": {
    "narrative": [{"src": "RootData|CoinGecko|AI", "fact": "关键支撑事实，15字以内", "fact_en": "Key supporting fact in English, ~15 words"}],
    "tech": [{"src": "GitHub|DeFiLlama|AI", "fact": "...", "fact_en": "..."}],
    "team": [{"src": "RootData|AI", "fact": "...", "fact_en": "..."}],
    "tokenomics": [{"src": "CoinGecko|Dune|AI", "fact": "...", "fact_en": "..."}],
    "data": [{"src": "DeFiLlama|Dune|CoinGecko", "fact": "...", "fact_en": "..."}],
    "compliance": [{"src": "RootData|AI", "fact": "...", "fact_en": "..."}]
  },
  "news_signals": {
    "sentiment": -1.0~1.0之间的数字 (-1=非常负面, 0=中性, 1=非常正面),
    "red_flags": ["从近期新闻里识别的负面信号，每条≤15字，如：SEC调查/团队成员离职/重大Hack/创始人被起诉/重大合作终止/项目暂停"],
    "green_flags": ["从近期新闻里识别的正面信号，每条≤15字，如：Binance上线/新战略合作/产品重大升级/获顶级VC加注/重大主网升级"],
    "summary": "用一句话(≤30字)概括近期新闻整体面貌",
    "summary_en": "One sentence (≤30 words) summarizing recent news",
    "red_flags_en": ["English version of each red flag, ≤15 words each"],
    "green_flags_en": ["English version of each green flag, ≤15 words each"]
  }
}

⚠️ news_signals 提取规则：
- 严格基于上方"近期新闻摘要"提取，不允许凭空捏造
- 没有相关新闻时：red_flags=[], green_flags=[], sentiment=0
- 区分日常更新（如普通公告）和真实信号（监管/合作/技术/团队/资金）
- 同一事件不要重复出现在红绿两边
- 优先识别可能影响价格/估值/投资决策的事件

🚨 必须识别的 RED 关键词（出现即必须放入 red_flags，sentiment ≤ -0.6）：
- 安全事件：hack / hacked / exploit / drained / breach / compromised / 漏洞 / 黑客 / 资金被盗 / 被盗
- 团队跑路：rugpull / rug pull / 跑路 / 套现 / dumping / sold all
- 监管打击：SEC sues / SEC charges / indictment / arrested / 被起诉 / 被捕 / 制裁 / sanctions / cease and desist
- 项目崩溃：insolvent / bankruptcy / shutdown / pause withdrawals / 暂停提现 / 破产
- 重大损失：$XXM loss / $XXM stolen / lost $XXM / 损失 / 暴跌 XX%
  → 这些事件必须出现在 red_flags，即使新闻标题是中性"项目方回应"措辞也要识别本质

✅ 必须识别的 GREEN 关键词（同步对称，避免遗漏正面信号）：
- 上线：Binance lists / Coinbase lists / 上线 Binance / mainnet launch
- 合作：partnership with [Tier-1 名] / strategic investment from [a16z/Paradigm/Binance Labs]
- 升级：major upgrade / V2 launch / token unlock cliff passed`;

export async function scoreProject(raw, onStatus) {
  onStatus("🤖 Claude AI 六维评分中...");
  const { details, fundingRounds, cgData, defiData, webIntel } = raw;
  const teamMembers = raw.teamMembers || [];
  const enhancedTeam = raw.enhancedTeam || null; // 团队增强（官网 + GitHub + Twitter + 摘要）
  const projectContext = raw.projectContext || null; // Step 2c: 项目画像合成
  const recentUpdatesSummary = raw.recentUpdatesSummary || null; // Step 1: 项目近况
  const docContent = raw.docContent || null;
  const duneAnalytics = raw.duneAnalytics || null;
  const cc = raw.complianceCheck || null;
  const am = raw.artemisMetrics || null; // Artemis 链上基本面
  const projectSocial = raw.teamSignals?.projectSocial || null; // Q26：项目主 X 账号
  const timeline = raw.timeline || []; // Q40：项目历史时间线（6 源合成 founded/github/firstRelease/wayback/firstDoc/firstFunding）
  const githubSignal = raw.githubSignal || null; // Q41：GitHub 客观活跃度（commits/stars/contributors）
  const topHolders = raw.topHolders || null; // Q43：链上持仓集中度（EVM 代币）
  const socialSignals = raw.socialSignals || null; // Q44：LunarCrush 社交叙事
  const duneTokenInfo = raw.duneTokenInfo || null; // Q45：Dune 链上流动性（pool size）
  // tokenUnlock / surfNews 现由 data-views 的 promptBlock 直接消费 raw 对象

  // ── 阶段化评估框架：自动识别项目阶段（多信号综合判断）──────────────
  const stageResult = detectStage(raw);
  const stageKey    = stageResult.stage;
  const stageConfig = STAGE_WEIGHTS[stageKey];
  const stageSignals = stageResult.signals || [];  // 判断依据列表
  const stageSignals_en = stageResult.signals_en || [];
  const stageWeights = {
    narrative: stageConfig.narrative, tech: stageConfig.tech, team: stageConfig.team,
    tokenomics: stageConfig.tokenomics, data: stageConfig.data, compliance: stageConfig.compliance,
  };

  // ── 改进1：融资金额≠估值，若估值未披露则按稀释比推算 ──────────────
  const latestRound = fundingRounds[0];
  const latestValuation = latestRound?.valuation || 0;
  const latestAmount    = latestRound?.amount    || 0;
  // 若无估值但有融资金额，按 20-25% 稀释率推算隐含估值（保守取25%）
  const impliedValuation = latestValuation > 0
    ? latestValuation
    : latestAmount > 0 ? latestAmount / 0.25 : 0;
  const valuationNote = latestValuation > 0
    ? `已披露估值 ${fmt(latestValuation)}`
    : latestAmount > 0 ? `估值未披露，按25%稀释推算约 ${fmt(impliedValuation)}（仅供参考）`
    : "无估值信息";

  // ── 改进5：识别顶级VC领投 ─────────────────────────────────────────
  const TOP_VCS = ["a16z","andreessen","paradigm","binance labs","yzi labs","pantera","coinbase ventures","sequoia","dragonfly","multicoin","polychain","spartan","framework","electric capital"];
  const leadInvestors = (details.investors || []).filter(i => i.lead_investor).map(i => i.name.toLowerCase());
  const hasTopVC = leadInvestors.some(n => TOP_VCS.some(t => n.includes(t)));

  // ── 改进3：按融资阶段判断是否为早期项目 ─────────────────────────────
  const roundName = (latestRound?.rounds || "").toLowerCase();
  const isEarlyStage = roundName.includes("pre-seed") || roundName.includes("preseed") || roundName.includes("angel");
  const isSeedStage  = roundName.includes("seed");

  // ── 预计算量化指标（作为客观锚点传入 prompt）──────────────────
  const fdv = cgData?.market_data?.fully_diluted_valuation?.usd || 0;
  const tvl = defiData?.tvl?.current || 0;
  const vol24h = cgData?.market_data?.total_volume?.usd || 0;
  const fdvTvlRatio  = fdv > 0 && tvl > 0   ? (fdv / tvl).toFixed(1)    : null;
  const fdvVolRatio  = fdv > 0 && vol24h > 0 ? (fdv / vol24h).toFixed(1) : null;

  // Protocol Economics：P/S = FDV / 年化手续费
  // DeFiLlama fees.monthly = 协议产生的全部手续费（30 天，用户支付）
  // P/S 是标准协议估值倍数，越低代表手续费越丰厚相对估值越合理
  const fees30d   = defiData?.fees?.monthly || 0;
  const annualFees = fees30d > 0 ? fees30d * 12 : 0;
  const psRatio   = fdv > 0 && annualFees > 0 ? (fdv / annualFees).toFixed(1) : null;

  // ── Q38: 统一 Revenue 信号（多源 + 显式来源标注） ─────────────
  // Hyperliquid 案例（2026-06-24）：defiData.fees=undefined（slug=hyperliquid-bridge 无数据），
  // 但 artemisMetrics.perpFees30d / revenue30d 实测有数据（路径 DefiLlama hyperliquid-perps），
  // ai.js line 260 取了 `am` 变量但 prompt 完全没消费 → AI 凭空给 MC/Rev 18.8x。
  //
  // 这里把 3 个来源按优先级合并：
  //   1) artemisMetrics（最准，针对衍生品类项目特化）
  //   2) defiData.revenue（DefiLlama 通用协议 revenue）
  //   3) defiData.fees（fees 退化为 revenue 上限）
  // 每条数据都标 source，下游 prompt 必须复述「实测/AI 估算」。
  const revenueSignals = (() => {
    if (am?.revenue30d > 0) {
      return { annual: am.revenue30d * 12, source: 'artemis', sourceLabel: 'Artemis/DefiLlama 实测', monthly: am.revenue30d };
    }
    if (am?.perpFees30d > 0) {
      return { annual: am.perpFees30d * 12, source: 'artemis_fees', sourceLabel: 'Artemis 手续费实测（无收入分成）', monthly: am.perpFees30d };
    }
    if (defiData?.revenue?.monthly > 0) {
      return { annual: defiData.revenue.monthly * 12, source: 'defillama_rev', sourceLabel: 'DefiLlama Revenue 实测', monthly: defiData.revenue.monthly };
    }
    if (defiData?.fees?.monthly > 0) {
      return { annual: defiData.fees.monthly * 12, source: 'defillama_fees', sourceLabel: 'DefiLlama Fees 实测（无收入分成）', monthly: defiData.fees.monthly };
    }
    return null;
  })();
  // MC/Rev 标准：用 circulating MC（CG `market_cap`），不用 FDV
  const circulatingMC = cgData?.market_data?.market_cap?.usd || 0;
  const mcRevRatio = (revenueSignals?.annual > 0 && circulatingMC > 0)
    ? (circulatingMC / revenueSignals.annual).toFixed(1)
    : null;

  // 信息质量评估
  const hasTeamInfo    = teamMembers.length > 0 || (webIntel?.founders?.length > 0);
  const hasGithub      = !!(details?.github || details?.social_media?.github ||
                            webIntel?.techIntel?.github_activity?.includes('活跃') ||
                            webIntel?.techIntel?.github_activity?.includes('Active'));
  const hasValuation   = !!(details?.total_funding > 0 || cgData?.market_data?.fully_diluted_valuation?.usd > 0);
  const complianceQueryFailed = cc && Object.values(cc).every(v => v?.status === 'error' || v?.status === 'failed');

  // ── 项目专属量化信号（tokenomics 分档判断 + 信息质量惩罚触发条件）──────
  // 2026-07-28：这些判断原来内联写在【评分基准规则】静态块中间，现在那块要整体
  // 挪到 SCORE_STATIC_RULES 做 prompt cache 前缀，所以把"跟项目相关、会变化"的
  // 这几行判断摘出来单独算，通过 prompt 里的指示文字引用（"按项目专属量化信号
  // 部分执行"），逻辑和原来的判断条件/文案完全一致，只是搬了个位置。
  const quantSignalsBlock = [
    fdvTvlRatio ? `- 当前 FDV/TVL = ${fdvTvlRatio}x → ${
      Number(fdvTvlRatio) < 5   ? '优秀（<5x），tokenomics 基础80' :
      Number(fdvTvlRatio) < 20  ? '合理（5-20x），tokenomics 基础70' :
      Number(fdvTvlRatio) < 60  ? '偏高（20-60x），tokenomics 基础55，需说明理由才能上调' :
      '严重高估（>60x），tokenomics 基础40，必须在notes中说明原因'
    }` : '',
    fdvVolRatio && !fdvTvlRatio ? `- 无 TVL，FDV/日交易量 = ${fdvVolRatio}x → ${
      Number(fdvVolRatio) < 30  ? '合理（<30x），tokenomics 基础70' :
      Number(fdvVolRatio) < 100 ? '偏高（30-100x），tokenomics 基础55' :
      '严重高估（>100x），tokenomics 基础40'
    }` : '',
    !fdvTvlRatio && !fdvVolRatio && details?.token_symbol ? '- 已发币但无 TVL/交易量数据：tokenomics 基础50（流动性不足）' : '',
    psRatio ? `- Protocol Economics P/S = ${psRatio}x（FDV ÷ 年化手续费）→ ${
      Number(psRatio) < 5   ? '极低估值（<5x），协议手续费丰厚，tokenomics 额外+5' :
      Number(psRatio) < 20  ? '合理（5-20x），收入支撑估值，tokenomics 额外+3' :
      Number(psRatio) < 50  ? '偏高（20-50x），存在成长溢价，tokenomics 中性' :
      Number(psRatio) < 100 ? '高估（50-100x），手续费不足以支撑估值，tokenomics -3' :
      '严重高估（>100x），收费规模远低于估值，tokenomics -5'
    }` : '',
    complianceQueryFailed ? '- 合规数据库全部查询失败：compliance -3（无法核实）' : '',
    !hasTeamInfo   ? 'ℹ️ 无团队公开信息（数据覆盖缺口，非负面证据）：team 按中性默认给分，不做惩罚性下调，notes.team 需注明"信息未公开"' : '',
    !hasGithub     ? '⚠️ 无可验证 GitHub 仓库：tech 维度上限 65 分' : '',
    !hasValuation  ? '⚠️ 估值/融资完全未披露：tokenomics 最高 68 分（无法定价）' : '',
  ].filter(Boolean).join('\n');

  // Q29：pre-TGE 检测 — 无 token symbol 且 CG/DeFiLlama 都没数据
  // 此时 tokenomics / data 维度的"主信号"应自动切换到 social / team / context
  const isPreTGE = (
    !details?.token_symbol &&
    !cgData?.market_data?.fully_diluted_valuation?.usd &&
    !defiData?.tvl?.current
  );

  const prompt = `⚠️ 项目阶段识别：【${stageConfig.label}】
评分框架：${stageConfig.focus}
阶段说明：${stageConfig.desc}
本阶段评分权重：叙事${Math.round(stageConfig.narrative*100)}% · 技术${Math.round(stageConfig.tech*100)}% · 团队${Math.round(stageConfig.team*100)}% · 代币${Math.round(stageConfig.tokenomics*100)}% · 数据${Math.round(stageConfig.data*100)}% · 合规${Math.round(stageConfig.compliance*100)}%
${isPreTGE ? `
=== 🌱 PRE-TGE 自适应评分模式 ===
检测到：项目未发行代币、CoinGecko 无数据、DeFiLlama 无 TVL。
**禁止**用"无链上数据"、"未发币"、"无 FDV"作为打低分的理由。

各维度信号源切换：

▸ tokenomics 维度（pre-TGE）：
  - 主信号：projectContext.tokenomicsDisclosure + inferredAllocation
  - 完全披露分配（zh/vc/community/treasury 都有数）+ 团队+VC < 30%  →  75-90 分
  - 部分披露（只有总数 / 大类）  →  55-70 分
  - 完全无 tokenomics 披露  →  30-45 分（不要给 0）
  - vesting 已公开 → 加 5-10 分

▸ data 维度（pre-TGE）：
  - 主信号：projectSocial（项目主 X）+ enhancedTeam 团队 Twitter + CG community
  - followers > 100k + 互动率 > 0.3% → 75-90 分
  - followers 10k-100k + 互动率 0.1-0.5% → 55-70 分
  - followers 1k-10k → 40-55 分
  - followers < 1k 或互动率 < 0.05% → 25-40 分
  - 链上活动（testnet TVL / DAU）若有，加 5-15 分
  - GitHub 90 日提交 > 100 + 贡献者 ≥ 5 → 额外 +5

▸ team 维度（pre-TGE 加权）：
  - 主信号：enhancedTeam.qualityScore + 创始人 LinkedIn
  - 实名 + ex-big-tech / ex-知名项目 → 75-90 分
  - 全 stealth + 无 LinkedIn → 25-40 分
  - 该项目 team 维度本来权重就高，pre-TGE 时再额外提一档参考

▸ narrative / tech / compliance 维度：
  - 沿用现有打分逻辑，不受 pre-TGE 影响

⚠ notes 字段必须说明"实际用了哪些 fallback 信号"，例如：
  "tokenomics 维度基于 inferredAllocation: 团队 25% / VC 20%，分配披露完整"
  "data 维度基于 projectSocial: followers 45k, 互动率 0.4%，社区真实"
` : ''}

${stageKey === "early" ? `【早期评分规则 - 重要】
- data 维度：因项目尚无链上数据，直接给 60-70 分（中性分），不因"无数据"扣分
- tech 维度：基于白皮书/官网描述评估技术愿景和可行性，不要求产品落地
- narrative 和 team 是核心评分项，要严格把关
- tokenomics 看设计合理性（若已有代币模型），否则给 55-65 分中性分` : ""}
${stageKey === "growth" ? `【中期评分规则】
- tech 和 data 是核心维度，严格评估产品成熟度和链上数据健康度
- 有 TVL/交易量/用户数据的必须量化引用
- 缺少链上数据时 data 维度应给 40-55 分（反映早期产品阶段）
- narrative 权重降低，因为已有数据验证产品市场契合度` : ""}
${stageKey === "tge_post" ? `【TGE后评分规则 - 重要】
- tokenomics 是最核心维度：FDV合理性、流通比例、解锁压力、通胀率
- data 维度看：24h 交易量、持币地址数、交易所分布、价格趋势
- narrative 和 team 权重下调，因为代币已定价，市场已部分消化这些因素
- 重点识别：是否存在大额解锁压力、流动性是否充足` : ""}
${docContent && !details.one_liner && !details.description ? '\n⚡ 纯文档分析模式：无RootData数据，请完全基于下方上传的文档内容进行评分，不要因"无数据"扣分，而应从文档中挖掘信息作为评分依据。' : docContent ? '\n⚡ 用户上传了项目文档。请优先参考文档内容进行分析，文档信息优先于AI推断，请在评语中引用文档中的具体数据。' : ''}
${(() => {
  // ⚡ GROUND TRUTH 块 — 已知量化事实，严禁用别的数字代替
  // 历史教训（2026-06-18）：AI 即使在 prompt 后段读到 "DeFiLlama TVL: \$441M" 仍写
  // "TVL=\$0" 进 notes.data / summary（hallucination）。在 prompt 顶部用显眼标记块
  // 重述关键数字，并写明禁止覆盖规则。
  const facts = [];
  if (defiData?.tvl?.current > 0) facts.push(`- DeFiLlama TVL：${fmt(defiData.tvl.current)}${defiData.tvl.weekChange != null ? `（7日 ${Number(defiData.tvl.weekChange) >= 0 ? '+' : ''}${defiData.tvl.weekChange}%）` : ''}（不可写 0/未知/无数据）`);
  if (defiData?.volume?.daily > 0) facts.push(`- DeFiLlama 24h DEX 成交量：${fmt(defiData.volume.daily)}（不可写 0/未知）`);
  if (defiData?.fees?.daily > 0) facts.push(`- DeFiLlama 日费用：${fmt(defiData.fees.daily)}（月费 ${fmt(defiData.fees.monthly || defiData.fees.daily * 30)}, 不可写 0/未知）`);
  if (cgData?.market_data?.fully_diluted_valuation?.usd > 0) facts.push(`- CoinGecko FDV：${fmt(cgData.market_data.fully_diluted_valuation.usd)}`);
  if (cgData?.market_data?.total_volume?.usd > 0) facts.push(`- CoinGecko 全市场 24h 成交：${fmt(cgData.market_data.total_volume.usd)}`);
  if (!facts.length) return '';
  return `\n=== 🎯 GROUND TRUTH（强制使用以下精确数字，禁止改写为 0/未知/无）===\n${facts.join('\n')}\n=== 评语 / summary / data_evidence / risks 引用以上数字必须原样，禁止用 "\$0" "TVL 为零" "无数据" 等表述替代 ===\n`;
})()}

=== ⚠️ 输出强制约束（违反 = 任务失败）===
你必须输出三个**配对结构**字段（zh/en 同条同对象，物理上不可能漏写一种语言）：
1. catalysts_paired: [{zh, en}, {zh, en}, {zh, en}] — 未来 3-6 月可能引爆估值的"具体事件"（如：主网升级、币安上线、合作公告、解锁、监管节点），区别于现有优势
2. strengths_paired: [{zh, en}, {zh, en}, {zh, en}] — 当前已经存在的优势
3. risks_paired: [{zh, en}, {zh, en}, {zh, en}] — 风险点
每组**恰好 3 个对象**，每个对象必须同时包含 zh 和 en 两个字段，zh 写中文 en 写英文，两个字段描述同一条事件/优势/风险。
三类语义不重合：catalysts 看未来事件 / strengths 看现在 / risks 看下行。
缺任何一组、对象缺 zh 或 en 字段、或不足 3 条，均视为 JSON schema 违例。请在生成完 fdv_tiers 等大对象前确保已输出三组 paired 数组。

=== ⚠️ unlock_schedule 反编造规则 ===
unlock_schedule 字段必须真实，不准编。决策树：
1) 输入里有 Surf 真实解锁数据（raw.tokenUnlock.ok=true）→ **直接用 raw.tokenUnlock.events，不要套模板**
2) 项目官方披露 / 白皮书 / projectContext.tokenomicsDisclosure.vestingDisclosed=true → 引用具体披露内容
3) 以上两条都没有 → **必须返回空数组 []**

禁止使用以下模板话术（已检测会被后处理置空）：
  ❌ "TGE，流通盘X%释放"
  ❌ "投资人锁仓到期，开始线性释放"
  ❌ "生态激励释放高峰"

写真实事件时每条必须包含具体百分比 / 月份 / 来源依据，例如:
  ✅ {"month": 18, "event": "Series A 投资人 30% 解锁（Surf 数据，2026-12-01）", "pressure": "high"}

=== 输出顺序参考 schema 模板，注意顺序里 catalysts_paired 在最前 ===

项目名称: ${details.project_name}
一句话: ${details.one_liner || "无"}
描述: ${(details.description || "").slice(0, 400)}
赛道: ${(details.tags || []).join(", ")}
成立: ${details.establishment_date || "未知"}
代币: ${details.token_symbol || "未发行"}
总融资: ${details.total_funding ? fmt(details.total_funding) : "未知"}
顶级VC领投: ${hasTopVC ? "是（" + leadInvestors.filter(n => TOP_VCS.some(t => n.includes(t))).join(", ") + "）" : "否"}
投资方: ${(details.investors || []).map(i => i.name).slice(0, 8).join(", ") || "无"}
融资阶段: ${isEarlyStage ? "Pre-Seed/天使（极早期）" : isSeedStage ? "Seed（早期）" : latestRound?.rounds || "未知"}
融资轮次: ${(Array.isArray(fundingRounds) ? fundingRounds : []).slice(0, 5).map(r => `${r.rounds}(${r.published_time},${fmt(r.amount)},估值${fmt(r.valuation)})`).join("; ") || "无"}
估值参考: ${valuationNote}
${buildValuationPromptBlock(raw)}
CoinGecko FDV: ${cgData?.market_data?.fully_diluted_valuation?.usd ? fmt(cgData.market_data.fully_diluted_valuation.usd) : "无"}
CoinGecko 24h成交量: ${cgData?.market_data?.total_volume?.usd ? fmt(cgData.market_data.total_volume.usd) : "无"}
${(() => {
  // Q27：CoinGecko community 数据透传（Telegram / Reddit 用户数）
  const cd = cgData?.community_data;
  if (!cd) return '';
  const tg = cd.telegram_channel_user_count;
  const rd = cd.reddit_subscribers;
  const lines = [];
  if (tg > 0) lines.push(`CoinGecko Telegram 频道用户数: ${tg.toLocaleString()}`);
  if (rd > 0) lines.push(`CoinGecko Reddit 订阅数: ${rd.toLocaleString()}`);
  return lines.length > 0 ? lines.join('\n') + '\n' : '';
})()}
DeFiLlama TVL: ${defiData?.tvl?.current ? fmt(defiData.tvl.current) + (defiData.tvl.weekChange ? `（7日${defiData.tvl.weekChange > 0 ? "+" : ""}${defiData.tvl.weekChange}%）` : "") : "无数据"}
DeFiLlama 日交易量: ${defiData?.volume?.daily ? fmt(defiData.volume.daily) : "无数据"}
DeFiLlama 日费用: ${defiData?.fees?.daily ? fmt(defiData.fees.daily) : "无数据"}
DeFiLlama 月手续费: ${defiData?.fees?.monthly ? fmt(defiData.fees.monthly) : "无数据"}
DeFiLlama 月协议收入: ${defiData?.revenue?.monthly ? fmt(defiData.revenue.monthly) : "无数据（协议无分成或DeFiLlama未追踪）"}
Protocol Economics P/S: ${psRatio ? psRatio + 'x（FDV÷年化手续费）' : "无数据（未发币或DeFiLlama无手续费数据）"}
${(() => { const rev = defiData?.revenue?.monthly; const fdv = cgData?.market_data?.fully_diluted_valuation?.usd; if (!rev || !fdv) return ''; const pRev = fdv / (rev * 12); return `Protocol Economics P/Rev: ${pRev.toFixed(1)}x（FDV÷年化收入，< 20x 被低估）`; })()}

【💰 GROUND TRUTH · Revenue 多源合并（禁止用其他数字覆盖）】
${revenueSignals ? `年化 Revenue: ${fmt(revenueSignals.annual)}（月度 ${fmt(revenueSignals.monthly)}）
数据来源: ${revenueSignals.sourceLabel} [source=${revenueSignals.source}]
${circulatingMC > 0 ? `Circulating MC: ${fmt(circulatingMC)}` : 'Circulating MC: 无 CG 数据'}
${mcRevRatio ? `**MC/Rev = ${mcRevRatio}x（实测）** — recommendation 中如要引用此倍率，必须复述「实测」标签` : 'MC/Rev: 无法计算（缺 MC 或 Rev）'}` : `Revenue 无任何实测来源（Artemis / DefiLlama 都未返回）。
⚠️ recommendation 中**禁止**给出具体 MC/Rev 或 FDV/Rev 倍率数字 — 没 ground truth 就不要凭空算。
如必须表达，用相对语言：「估值需结合 Revenue 数据评估」「Revenue 数据缺失，倍率无法计算」。`}
Dune DEX 30日总交易量: ${duneAnalytics ? fmt(duneAnalytics.total_volume) : "无数据"}
Dune DEX 30日总交易笔数: ${duneAnalytics ? duneAnalytics.total_trades.toLocaleString() : "无数据"}
Dune DEX 日均交易量: ${duneAnalytics?.days?.length ? fmt(duneAnalytics.total_volume / duneAnalytics.days.length) : "无数据"}
Dune DEX 日均活跃交易者(DAU): ${(() => { const days = duneAnalytics?.days; const isHL = duneAnalytics?.source === 'hyperliquid_market_data'; if (!days?.length || isHL) return '无数据'; const avg = Math.round(days.reduce((s, d) => s + (d.unique_traders || 0), 0) / days.length); return avg > 0 ? avg.toLocaleString() + '（30日均值，dex.trades distinct taker）' : '无数据'; })()}
Dune DEX 链: ${duneAnalytics?.chain || "无数据"}
${buildProtocolPromptBlock(raw)}
${buildUnlockPromptBlock(raw)}
${buildHoldersPromptBlock(raw)}
${buildSocialPromptBlock(raw)}
${buildNewsPromptBlock(raw)}
【合规数据库查询结果（实时检索，非 AI 推断）】
OFAC 制裁名单: ${!cc ? "未查询" : cc.ofac?.status === 'hit' ? `⚠️ 发现匹配：${cc.ofac.hits.join(", ")}` : cc.ofac?.status === 'clear' ? "✅ 未发现制裁记录" : "查询失败"}
SEC 执法记录: ${!cc ? "未查询" : cc.sec?.status === 'found' ? `⚠️ 发现 ${cc.sec.records.length} 条相关记录` : cc.sec?.status === 'clear' ? "✅ 未发现执法记录" : "查询失败"}
FCA 注册状态: ${!cc ? "未查询" : cc.fca?.status === 'warning' ? "🔴 在 FCA 警告名单内" : cc.fca?.status === 'registered' ? `✅ FCA 注册实体（${cc.fca.firms.map(f => f.name).join(", ")}）` : cc.fca?.status === 'not_found' ? "未发现 FCA 注册" : "查询失败"}
FinCEN MSB 注册: ${!cc ? "未查询" : cc.fincen?.status === 'registered' ? `✅ MSB 注册（${cc.fincen.registrants.map(r => r.name).join(", ")}）` : cc.fincen?.status === 'not_found' ? "未发现 MSB 注册" : "查询失败"}
合规查询说明: 未在数据库发现记录≠合规，协议类/早期项目通常无需 MSB 注册；若有制裁或执法记录则合规维度应显著扣分
生态: ${(details.ecosystem || []).slice(0, 3).join(", ") || "未知"}

【项目专属量化信号 — tokenomics/信息质量维度判断依据，按下方分档执行】
${quantSignalsBlock || "（无需额外量化判断——未发币，或数据不足以计算 FDV/TVL 等比率）"}

【网站情报数据 - 来自官网爬取和流量分析】
官网月访问量: ${webIntel?.techIntel?.traffic?.monthly_visits_estimate || "无数据"}
流量规模: ${webIntel?.techIntel?.traffic?.traffic_tier || "无数据"}
主要流量地区: ${webIntel?.techIntel?.traffic?.top_regions?.join(", ") || "无数据"}
增长趋势: ${webIntel?.techIntel?.traffic?.growth_trend || "无数据"}
创始人/团队Twitter: ${webIntel?.founderTwitters?.length ? webIntel.founderTwitters.map(t => "@" + (t.handle||t)).join(", ") : "未找到"}
创始人背景:
${webIntel?.founders?.length ? webIntel.founders.slice(0, 4).map(f => `- ${f.name}${f.twitter ? " (@"+f.twitter.split("/").filter(Boolean).pop()+")" : ""}: ${(f.introduce || "无介绍").slice(0, 110)}`).join("\n") : "无创始人数据"}
团队成员（来自RootData）: ${teamMembers.length > 0 ? teamMembers.slice(0, 6).map(m => `${m.name}${m.title ? "(" + m.title + ")" : ""}${m.description ? " - " + m.description.slice(0, 60) : ""}`).join(" | ") : "无数据"}
团队增强（官网/GitHub/Twitter 抓取）: ${enhancedTeam?.founders?.length > 0
  ? enhancedTeam.founders.slice(0, 8).map(f => {
      const tw = f.twitter ? ` tw=${f.twitter.replace(/^@/, '')}` : '';
      const gh = f.github  ? ` gh=${f.github}` : '';
      const prior = f.priorWork?.length ? ` 前作=[${f.priorWork.slice(0, 2).join(', ')}]` : '';
      const bio = f.bio ? ` 简介=${f.bio.slice(0, 60)}` : '';
      return `${f.name}(${f.role || '?'}${tw}${gh})${prior}${bio}`;
    }).join(' | ')
  : (enhancedTeam ? `已扫描但无识别（来源: ${(enhancedTeam.sourcesUsed || []).join(',') || '无'}）` : '未启用')
}${enhancedTeam?.topContributors?.length > 0 ? `\nGitHub 前 ${enhancedTeam.topContributors.length} 贡献者: ${enhancedTeam.topContributors.map(c => `@${c.username}(${c.commitCount}提交,${c.followers}粉丝${c.bio ? ', '+c.bio.slice(0,40) : ''})`).join(' | ')}` : ''}${enhancedTeam?.summary ? `\n团队质量综合（合成层）: 评分=${enhancedTeam.summary.qualityScore}/100, 实名=${enhancedTeam.summary.hasDoxxedFounders ? '是' : '否'}, 摘要="${enhancedTeam.summary.oneLineSummary}", 强项=[${(enhancedTeam.summary.strengths || []).join('; ')}], 关注=[${(enhancedTeam.summary.concerns || []).join('; ')}]` : ''}
⚠ 团队评分注意：上方"团队增强"列出真实团队人员时，团队维度不可标"匿名"或"无团队信息"。
应根据实名度 / 前作含金量 / 工程师人数 / GitHub 活跃度 / 合成层 qualityScore 综合给分。
${enhancedTeam?.companyStats ? `\n【LinkedIn 公司统计】员工 ${enhancedTeam.companyStats.totalEmployeesSeen} | 工程 ${enhancedTeam.companyStats.engineeringCount} | 业务 ${enhancedTeam.companyStats.businessCount}${enhancedTeam.companyStats.hiringSignal ? ' | ⭐ 招聘信号' : ''}
⚠ pre-TGE team 参考: 工程≥5+招聘=扩张; 工程≥业务2x=tech-heavy; 1-2人=早期` : ''}

${projectSocial ? `\n【项目主 X 账号 — data 维度核心】
@${projectSocial.handle}（${(projectSocial.ageYears || 0).toFixed(1)}年）| 关注 ${projectSocial.followers != null ? projectSocial.followers.toLocaleString() : '?'} | 30日推文 ${projectSocial.tweets30dCount || 0}
中位互动: 👍${projectSocial.medianEngagement?.likes ?? '?'} 🔁${projectSocial.medianEngagement?.retweets ?? '?'} 💬${projectSocial.medianEngagement?.replies ?? '?'} | 互动率 ${projectSocial.engagementRate != null ? projectSocial.engagementRate + '%' : '?'}
${projectSocial.telegram?.memberCount != null ? `TG @${projectSocial.telegram.handle}: ${projectSocial.telegram.memberCount.toLocaleString()}人 (${projectSocial.telegram.type})` : ''}
⚠ pre-TGE data 主信号 | follower: <1k 弱/1k-10k 中/10k-100k 强/>100k 头部 | 互动率: <0.1% 僵尸/>0.5% 真社区
` : ''}
${projectContext ? `\n【项目画像合成 — 评 tech/data/narrative 必看】
阶段: ${projectContext.productMaturity?.stage || '?'}（${projectContext.productMaturity?.evidence || '?'}）
功能（${projectContext.productFeatures?.length || 0}个）: ${(projectContext.productFeatures || []).slice(0, 5).map(f => `${f.name}(${f.maturity})`).join('; ') || '无'}
技术栈: ${(projectContext.techStack || []).join(', ') || '?'} | 深度 ${projectContext.technicalDepth?.score ?? '?'}/10（${projectContext.technicalDepth?.basis || '?'}）
近期节奏: ${projectContext.recentMomentum?.productEvents30d ?? 0}个事件 — ${projectContext.recentMomentum?.summary || '?'}
关键事件: ${(projectContext.recentMomentum?.keyEvents || []).slice(0, 3).join(' | ') || '无'}
竞争定位: ${projectContext.competitivePosition || '?'} | 证据: ${(projectContext.evidenceTags || []).join('、') || '无'}
已识别风险: ${(projectContext.knownRisks || []).slice(0, 3).join(' | ') || '无'} | 来源: ${(projectContext.sourcesUsed || []).join('+') || '无'}
${projectContext.tokenomicsDisclosure ? `代币披露: 白皮书=${projectContext.tokenomicsDisclosure.hasWhitepaper ? '有' : '无'} / 分配=${projectContext.tokenomicsDisclosure.allocationDisclosed ? '是' : '否'} / vesting=${projectContext.tokenomicsDisclosure.vestingDisclosed ? '是' : '否'} / 源=${projectContext.tokenomicsDisclosure.source || '?'}` : ''}
${projectContext.inferredAllocation ? `AI 抽取分配: 团队${projectContext.inferredAllocation.team || 0}%/VC${projectContext.inferredAllocation.vc || 0}%/社区${projectContext.inferredAllocation.community || 0}%/国库${projectContext.inferredAllocation.treasury || 0}%/基金${projectContext.inferredAllocation.foundation || 0}%（pre-TGE tokenomics 核心）` : ''}

⚠ 评分依据 (强制):
- tech: productMaturity/technicalDepth/techStack（禁笼统"技术活跃"）
- narrative: recentMomentum + competitivePosition
- data: evidenceTags + 链上数据
- tokenomics(pre-TGE): tokenomicsDisclosure + inferredAllocation; 团队/VC<35% 加分; 全无披露 警示而非 0
- knownRisks → risk_penalty` : ''}
${timeline.length >= 2 ? (() => {
  const events = timeline.slice(0, 6).map(e => `${(e.date || '').slice(0, 7)} ${e.label}`).join(' → ');
  const firstDate = timeline[0]?.date;
  const lastDate = timeline[timeline.length - 1]?.date;
  let span = '';
  if (firstDate && lastDate && firstDate !== lastDate) {
    const years = ((new Date(lastDate) - new Date(firstDate)) / (1000 * 60 * 60 * 24 * 365)).toFixed(1);
    span = ` (跨度 ${years} 年)`;
  }
  return `\n【项目历史时间线 — narrative 核心】
${events}${span}
⚠ 判断发展节奏 / 真实历史长度 / Bootstrap vs VC-driven`;
})() : ''}
${githubSignal ? (() => {
  const parts = [];
  if (githubSignal.owner && githubSignal.repo) parts.push(`仓库 ${githubSignal.owner}/${githubSignal.repo}`);
  else if (githubSignal.owner) parts.push(`org ${githubSignal.owner}`);
  if (githubSignal.stars != null) parts.push(`⭐ ${githubSignal.stars}`);
  if (githubSignal.forksCount != null) parts.push(`fork ${githubSignal.forksCount}`);
  if (githubSignal.contributorCount != null) parts.push(`贡献者 ${githubSignal.contributorCount}`);
  if (githubSignal.commits4w != null) parts.push(`4周 ${githubSignal.commits4w}c`);
  if (githubSignal.lastPushed) parts.push(`末次 ${githubSignal.lastPushed.slice(0, 10)}`);
  if (githubSignal.isFork) parts.push('⚠️ Fork');
  return `\n【GitHub 活跃度 — tech 维度实测，禁凭空给 commit 数】
${parts.join(' | ')}
⚠ commits4w >20 活/5-20 正常/<5 停 | ⭐ >5K 知名/1-5K 成长/<1K 早期 | 贡献者 >3 团队/1 单人 | isFork→tech≤60`;
})() : ''}
${topHolders ? (() => {
  const conc = topHolders.top10Concentration;
  const top5 = (topHolders.holders || []).slice(0, 5).map(h => {
    const id = h.entityName || (h.address ? h.address.slice(0, 8) + '…' : '?');
    return `${id} ${h.percentage}%`;
  }).join(' | ');
  const recognizedCount = (topHolders.holders || []).filter(h => h.entityName).length;
  return `\n【链上 Top10 持仓集中度（${topHolders.chain}）— token 维度实测信号，禁凭空给集中度】
Top10 占总供应: ${conc}% | 前 5: ${top5}
已识别实体: ${recognizedCount}/10（CEX/基金会等）| 未知地址 ${10 - recognizedCount} 个
⚠ <30% 分散健康/30-50% 中等/50-70% 偏集中/>70% 高度集中(风险) | 已识别 CEX/基金会算流通; 未知地址 = 黑盒（可能 team/VC/whale）`;
})() : ''}
${socialSignals ? `\n【LunarCrush 社交叙事 — narrative 维度实测】
Galaxy Score ${socialSignals.galaxyScore ?? '?'}/100 | Alt Rank ${socialSignals.altRank ?? '?'} | 24h 社交量 ${socialSignals.socialVolume ?? '?'} | 社交份额 ${socialSignals.socialDominance != null ? socialSignals.socialDominance + '%' : '?'} | 情感 ${socialSignals.sentiment ?? '?'}/100
⚠ Galaxy >70 强叙事/40-70 一般/<40 弱 | Alt Rank 越小越强 | Sentiment >70 看多/<30 看空` : ''}
${duneTokenInfo ? `\n【Dune 链上流动性（${duneTokenInfo.chain}）— token 维度补充】
DEX 池深度: ${duneTokenInfo.poolSize != null ? fmt(duneTokenInfo.poolSize) : '?'} | 实测价 $${duneTokenInfo.priceUsd != null ? duneTokenInfo.priceUsd.toFixed(4) : '?'} | 池类型 ${duneTokenInfo.poolType || '?'}
⚠ 池深度 >$5M 充裕/$500K-5M 中等/<$500K 流动性弱（小额买卖即影响价格）` : ''}
${recentUpdatesSummary?.updates?.length ? `\n【项目近况 30 天 — narrative + risks】
${recentUpdatesSummary.updates.length}条要点（${(recentUpdatesSummary.keyThemes || []).join('、')}）: ${recentUpdatesSummary.updates.slice(0, 5).map(u => `[${u.date}|${u.category}|${u.sentiment}] ${u.title}`).join(' / ')}
整体: ${recentUpdatesSummary.oneLineSummary || ''}` : ''}
白皮书/文档摘要: ${webIntel?.whitepaperSummary ? webIntel.whitepaperSummary.slice(0, 550) : "无"}
${docContent ? `\n【📎 上传文档（请重点参考）】\n${docContent.slice(0, 12000)}` : ''}
官网产品描述: ${webIntel?.websiteKeywords?.[0] || "无"}

【技术产品深度分析（Claude二次分析结果）】
产品功能: ${webIntel?.techIntel?.product_features?.slice(0, 5).join(", ") || "未知"}
技术栈: ${webIntel?.techIntel?.tech_stack?.slice(0, 6).join(", ") || "未知"}
产品阶段: ${webIntel?.techIntel?.product_stage || "未知"}
差异化优势: ${webIntel?.techIntel?.differentiators?.slice(0, 4).join("; ") || "未知"}
GitHub活跃度: ${(webIntel?.techIntel?.github_activity || "未知").slice(0, 200)}
竞品对比: ${(webIntel?.techIntel?.competitor_comparison || "未知").slice(0, 250)}
可量化数据: ${webIntel?.techIntel?.data_evidence?.slice(0, 5).join(", ") || "无"}
技术评分依据: ${(webIntel?.techIntel?.tech_score_basis || "无").slice(0, 200)}`;

  const res = await fetch(`${AI_BASE}/api/ai/score`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      model: "claude-opus-4-7",
      max_tokens: 6000,  // 之前 4000 偶发漏 risks/strengths/catalysts（schema 末段），加 50% breathing room
      // Q62 验证：output 大小不是 lanyi_403 触发因素（1500 仍 403）。限制在 input prompt 长度。
      // 2026-07-28：静态评分规则+schema+关键词表（SCORE_STATIC_RULES）单独成块并加
      // cache_control 断点，命中时按 ~10% 计费；prompt（本次项目专属动态数据）放
      // 断点之后。TTL 用 1h 而非默认 5min——这份缓存是全站共享的，只要 1 小时内
      // 有任何人触发任意项目的分析都会命中，1h 窗口下命中率远高于 5min，多付的
      // 写入成本（2x vs 1.25x）很容易被摊薄。
      messages: [{
        role: "user",
        content: [
          { type: "text", text: SCORE_STATIC_RULES, cache_control: { type: "ephemeral", ttl: "1h" } },
          { type: "text", text: prompt },
        ],
      }],
    }),
  }).catch(e => { throw new Error(`无法连接AI评分服务。检查 Railway 代理是否在线。`); });
  const ai = await res.json();
  console.log("[Claude API] status:", res.status, "response:", JSON.stringify(ai).slice(0, 500));
  // Q47: fallback 时打印根因（lanyi_timeout_35s / lanyi_4xx_N / lanyi_network_err）
  if (ai._fallback) {
    console.warn(`[Claude API] ⚠️ 走了 DeepSeek 兜底, 原因: ${ai._fallback_reason || '未知（旧版 proxy 没标注）'}`);
  }
  if (ai.error) {
    // proxy 504 fallback timeout 时 ai.error 是字符串 "DeepSeek timeout (18s)"，
    // Claude 标准错误时 ai.error 是 {type, message} 对象。两种都要兜底。
    const errStr = typeof ai.error === 'string'
      ? ai.error
      : (ai.error.message || ai.error.type || JSON.stringify(ai.error));
    // 504 timeout / fallback 类错误用户友好提示；其他错误保留技术细节
    if (res.status === 504 || /timeout/i.test(errStr)) {
      throw new Error(`AI 评分服务繁忙（${errStr}）。这通常是项目数据量过大导致 AI 处理超时。请稍后点 🔄 重试，或联系反馈。`);
    }
    throw new Error(`AI 评分失败: ${errStr}`);
  }
  if (!res.ok) {
    // 非 200 但没有 error 字段（罕见）
    throw new Error(`AI 服务返回 HTTP ${res.status}。请稍后点 🔄 重试。`);
  }
  const txt = (ai.content || []).map(b => b.type === "text" ? b.text : "").join("");

  // ── 鲁棒 JSON 提取 ──────────────────────────────────────────────────────────
  // 问题根因：贪婪正则 /\{[\s\S]*\}/ 从第一个 { 开始匹配，当 Claude 在 JSON 前输出含
  // 花括号的说明文字（如 "{based on data}: {...}"）时，导致非法 JSON（position 1 错误）。
  // 修复：①Unicode 规范化（非标准空格/智能引号）②从 {"track" 精确定位 JSON 起点
  //       ③括号深度追踪提取完整对象 ④多层修复兜底。
  const rawTxt = txt
    // 非标准 Unicode 空格 → 普通空格（显式 \u 转义，防编辑器乱码）
    // NBSP \u00A0, Ogham \u1680, En-spaces \u2000-\u200B, \u202F, \u205F, Ideographic \u3000, BOM \uFEFF
    .replace(/[\u00A0\u1680\u2000-\u200B\u202F\u205F\u3000\uFEFF]/g, ' ')
    // 智能/弯引号 → 直引号（\u201C/D/E/F 左右双引号, \u2033/6 素引号, \uFF02 全角）
    .replace(/[\u201C\u201D\u201E\u201F\u2033\u2036\uFF02]/g, '"')
    // \u2018/9 左右单引号, \u201A/B 低/高单引号, \u2032/5 素撇号
    .replace(/[\u2018\u2019\u201A\u201B\u2032\u2035]/g, "'")
    // 去除 markdown 代码块标记
    .replace(/```json\s*/g, "").replace(/```\s*/g, "").trim();

  // 精确找到 JSON 起始位置
  // 用正则允许 { 与 " 之间有空白（NBSP 规范化后 {\u00A0"track" → { "track"）
  const _startMatch = rawTxt.match(/\{\s*"track"/)  // 评分 JSON 固定首字段
                   || rawTxt.match(/\{\s*"/)          // 兜底：第一个 JSON 对象起始
  const jsonStart = _startMatch ? _startMatch.index : -1;
  if (jsonStart === -1) throw new Error(`AI 返回格式异常（无 JSON 起点）: ${rawTxt.slice(0,300)}`);

  // 括号深度追踪：正确处理嵌套对象和字符串内的括号
  let depth = 0, inStr = false, esc = false, jsonEnd = -1;
  for (let i = jsonStart; i < rawTxt.length; i++) {
    const c = rawTxt[i];
    if (esc)               { esc = false; continue; }
    if (c === '\\' && inStr) { esc = true;  continue; }
    if (c === '"')           { inStr = !inStr; continue; }
    if (!inStr) {
      if (c === '{') depth++;
      else if (c === '}') { depth--; if (depth === 0) { jsonEnd = i; break; } }
    }
  }
  if (jsonEnd === -1) throw new Error(`AI 返回格式异常（JSON 未闭合）: ${rawTxt.slice(jsonStart, jsonStart+300)}`);

  const rawJson = rawTxt.slice(jsonStart, jsonEnd + 1);

  // 多层修复：尾逗号 / 字符串内换行 / 控制字符 / 缺逗号 / 字符串内嵌未转义引号
  let parsed;
  const attempts = [
    () => JSON.parse(rawJson),
    () => JSON.parse(rawJson.replace(/,\s*([}\]])/g, '$1')),
    () => JSON.parse(rawJson.replace(/:\s*"((?:[^"\\]|\\.)*)"/gs,
      (_, v) => ': "' + v.replace(/\n/g, '\\n').replace(/\r/g, '\\r').replace(/\t/g, '\\t') + '"')),
    () => JSON.parse(rawJson.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '')),
    () => JSON.parse(insertMissingCommas(rawJson)),
    // 复合修复：缺逗号 + 尾逗号 + 控制字符 全套兜底
    () => JSON.parse(
      insertMissingCommas(rawJson)
        .replace(/,\s*([}\]])/g, '$1')
        .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '')
    ),
    // attempt 7：字符串内嵌未转义引号（如中文正文里直接打"术语"强调）
    () => JSON.parse(escapeStrayQuotes(rawJson)),
    // 复合修复：内嵌引号 + 缺逗号 + 尾逗号 + 控制字符 全套兜底
    () => JSON.parse(
      insertMissingCommas(escapeStrayQuotes(rawJson))
        .replace(/,\s*([}\]])/g, '$1')
        .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '')
    ),
  ];
  let lastErr;
  for (const attempt of attempts) {
    try { parsed = attempt(); break; } catch (e) { lastErr = e; }
  }
  if (!parsed) throw new Error(`JSON 解析失败: ${lastErr?.message} | raw(500): ${rawJson.slice(0, 500)}`);

  // ── scores 缺失兜底（Solstice 2026-06-23 事故）：DeepSeek 在 prompt 截断或
  //    paired schema 扩散后偶发不输出 scores 对象。先尝试从扁平字段恢复
  //    （narrative_score / narrativeScore / scores_narrative），全失败则
  //    占位 + _scores_missing flag → UI 显示"AI 未生成评分"banner，避免
  //    composite=0 被误读成"项目真有 0 分"。
  const DIM_KEYS = ['narrative', 'tech', 'team', 'tokenomics', 'data', 'compliance'];
  if (!parsed.scores || typeof parsed.scores !== 'object' || Array.isArray(parsed.scores)) {
    const recovered = {};
    for (const dim of DIM_KEYS) {
      const candidates = [
        parsed[`${dim}_score`], parsed[`score_${dim}`], parsed[`${dim}Score`],
        parsed[`scores_${dim}`], parsed?.[`${dim}`],
      ];
      const found = candidates.find(v => typeof v === 'number' && !Number.isNaN(v));
      if (found != null) recovered[dim] = found;
    }
    if (Object.keys(recovered).length >= 4) {
      parsed.scores = { narrative: 0, tech: 0, team: 0, tokenomics: 0, data: 0, compliance: 0, ...recovered };
      parsed._scores_recovered = true;
      console.warn(`[ai-score-shape] scores 缺失，从扁平字段恢复 ${Object.keys(recovered).length} 维:`, recovered);
    } else {
      parsed.scores = { narrative: 0, tech: 0, team: 0, tokenomics: 0, data: 0, compliance: 0 };
      parsed._scores_missing = true;
      console.error(`[ai-score-shape] scores 完全缺失且扁平字段也无法恢复 → 占位 0 + flag`);
    }
  }
  // 使用阶段权重计算 composite（比 DEFAULT_WEIGHTS 更反映项目实际情况）
  const composite = calcComposite(parsed.scores, stageWeights);

  // 防御性默认：DeepSeek（降级路径）和上游响应截断都可能漏返回 news_signals
  // （它在 prompt schema 的最后位置，最容易被切掉）。如果缺失，写一个空对象占位 —
  // verdict.js 用 newsSignalsState='empty' 显示"近期无重要新闻"，比"未读取"准。
  // 真正缺失上游新闻数据时也会落入这条路径，对 UI 友好且打破 projectCache rule 6 死循环。
  const news_signals = parsed.news_signals && typeof parsed.news_signals === 'object'
    ? parsed.news_signals
    : { red_flags: [], green_flags: [], sentiment: 0, summary: '', _defaulted: true };

  // ── 兜底 sanitize：删 "TVL $0" / "TVL 为零" / "无 TVL" 等明显与 ground truth 矛盾的文本
  // AI 偶发 hallucinate 即便 prompt 顶部写了 ground truth, 这里是最后一道防线
  const realTvl = defiData?.tvl?.current || 0;
  const realFees = defiData?.fees?.monthly || defiData?.fees?.daily || 0;
  const stripBadTvlPhrases = (s) => {
    if (typeof s !== 'string' || !s) return s;
    let out = s;
    if (realTvl > 0) {
      // "TVL $0" / "TVL 为 0" / "TVL=$0" / "TVL: 0" / "无 TVL" / "无 链上 TVL" / "DeFi TVL 为零"
      out = out
        .replace(/(链上\s*DeFi\s*)?TVL\s*[:=为是]*\s*[\$￥]?0\s*[万亿元美刀]?\b/gi, `TVL ${fmt(realTvl)}`)
        .replace(/无\s*(链上\s*)?TVL/gi, `TVL ${fmt(realTvl)}`)
        .replace(/(链上\s*)?TVL\s*为零/gi, `TVL ${fmt(realTvl)}`)
        .replace(/(链上\s*)?TVL\s*未?披?露/gi, `TVL ${fmt(realTvl)}`)
        .replace(/(链上\s*)?TVL\s*缺(?:失|乏)/gi, `TVL ${fmt(realTvl)}`);
    }
    if (realFees > 0) {
      out = out
        .replace(/(协议)?(费用|手续费|Fees?)\s*[:=为是]*\s*[\$￥]?0\s*[万亿元美刀]?\b/gi, `费用 ${fmt(realFees)}`);
    }
    return out;
  };
  const sanitizeShallow = (obj) => {
    if (!obj || typeof obj !== 'object') return obj;
    const out = Array.isArray(obj) ? obj.map(v => typeof v === 'string' ? stripBadTvlPhrases(v) : v) : { ...obj };
    if (!Array.isArray(obj)) {
      for (const k of Object.keys(out)) {
        if (typeof out[k] === 'string') out[k] = stripBadTvlPhrases(out[k]);
      }
    }
    return out;
  };
  if (parsed.summary)        parsed.summary        = stripBadTvlPhrases(parsed.summary);
  if (parsed.valuation_basis)parsed.valuation_basis= stripBadTvlPhrases(parsed.valuation_basis);
  if (parsed.notes)          parsed.notes          = sanitizeShallow(parsed.notes);

  // ── 类型强制：risks / strengths / catalysts 必须是 string[]
  // AI（特别是 DeepSeek）偶发返回 [{risk, severity}, ...] 对象数组
  // 直接渲染会触发 React error #31 把整个详情页打成空白。
  // 这里把对象项压扁成 "[SEVERITY] text" 字符串，是最后一道防线。
  const coerceStringList = (arr) => {
    if (!Array.isArray(arr)) return arr;
    return arr.map(item => {
      if (typeof item === 'string') return stripBadTvlPhrases(item);
      if (item == null) return '';
      if (typeof item === 'object') {
        const text = item.risk || item.strength || item.catalyst || item.text || item.description || item.title || item.label || '';
        const sev = item.severity || item.level || item.impact;
        const out = sev ? `[${String(sev).toUpperCase()}] ${text}` : text;
        return stripBadTvlPhrases(String(out || JSON.stringify(item)));
      }
      return String(item);
    }).filter(s => s && s.length > 0);
  };
  for (const k of ['risks', 'risks_en', 'strengths', 'strengths_en', 'catalysts', 'catalysts_en', 'tech_risks', 'tech_risks_en']) {
    if (parsed[k]) parsed[k] = coerceStringList(parsed[k]);
  }

  // ── paired schema 解码：AI 现在被要求输出 risks_paired: [{zh,en}, ...]，
  //    物理上无法漏写其中一种语言。这里把 paired 拆成下游统一使用的
  //    twin arrays (risks + risks_en)。AI 返回老格式时回退到原字段。
  for (const k of ['risks', 'strengths', 'catalysts']) {
    const pairedKey = `${k}_paired`;
    const paired = parsed[pairedKey];
    if (!Array.isArray(paired) || paired.length === 0) continue;
    const zhList = [];
    const enList = [];
    for (const pair of paired) {
      if (pair && typeof pair === 'object') {
        const zh = (pair.zh || '').toString().trim();
        const en = (pair.en || '').toString().trim();
        if (zh || en) {
          zhList.push(zh || en);   // 退化：若一边空就用另一边
          enList.push(en || zh);
        }
      }
    }
    if (zhList.length > 0) {
      // paired 输出优先；老 twin arrays 仅在 paired 完全缺失时使用
      parsed[k] = zhList;
      parsed[`${k}_en`] = enList;
      console.log(`[ai-score-shape] paired ${k} 解析: ${zhList.length} 对`);
    }
    // 解析完不删 paired 字段 — 留给 schema validator + Obsidian 调试看原值
  }

  // ── comparable_projects / fdv_tiers / unlock_schedule 强制 array
  //    DeepSeek 偶发把这些字段返回成 object（如 {项目1: {...}, 项目2: {...}}）
  //    UI 端 `a.comparable_projects.map(...)` 直接崩成白页（Ambient 案例）。
  //    与 risks/strengths/catalysts 同等的最后一道防线。
  for (const k of ['comparable_projects', 'fdv_tiers', 'unlock_schedule', 'exit_paths', 'exit_paths_en']) {
    const v = parsed[k];
    if (v != null && !Array.isArray(v)) {
      if (typeof v === 'object') {
        // 对象转数组：取所有 value（如果 value 本身是对象就保留）
        const arr = Object.values(v).filter(x => x != null);
        parsed[k] = arr;
        console.warn(`[ai-score-shape] ${k} 是对象不是数组，已转换为 ${arr.length} 项数组`);
      } else {
        parsed[k] = [];
        console.warn(`[ai-score-shape] ${k} 是 ${typeof v} 非法类型，已置空`);
      }
    }
  }

  // ── Q32: unlock_schedule 反编造检测 ────────────────────────────────
  //    AI 在没真实 tokenomics 数据时常常照抄 prompt 例子（"TGE 流通盘释放"
  //    "投资人锁仓到期" "生态激励释放高峰"）当真数据返回。检测这些模板
  //    话术并强制置空，避免用户被假数据误导做投资决策。
  //    例外：raw.tokenUnlock?.ok 为真时表示有 Surf 实测数据 — 不动。
  const hasRealUnlock = !!raw?.tokenUnlock?.ok && Array.isArray(raw.tokenUnlock.events) && raw.tokenUnlock.events.length > 0;
  if (!hasRealUnlock && Array.isArray(parsed.unlock_schedule) && parsed.unlock_schedule.length > 0) {
    const TEMPLATE_PHRASES = [
      '流通盘',                  // "TGE，流通盘X%释放"
      '投资人锁仓到期',          // "投资人锁仓到期，开始线性释放"
      '生态激励释放高峰',
      'initial circulation',
      'lock-up expires',
      'incentive release peak',
    ];
    const matches = parsed.unlock_schedule.filter(u => {
      const text = `${u?.event || ''} ${u?.event_en || ''}`.toLowerCase();
      return TEMPLATE_PHRASES.some(p => text.includes(p.toLowerCase()));
    }).length;
    // 3 条里命中 ≥ 2 条 = 显然是抄模板，整段置空
    if (matches >= 2) {
      console.warn(`[ai-score-shape] unlock_schedule 检测为 AI 模板复制（${matches}/${parsed.unlock_schedule.length} 命中模板词），且无 Surf 真实数据 — 置空避免误导`);
      parsed.unlock_schedule = [];
      parsed._unlock_schedule_source = 'cleared_by_template_detection';
    }
  }
  // 标注 source 给 UI 用：Q32 三层防御的 C 层
  if (Array.isArray(parsed.unlock_schedule) && parsed.unlock_schedule.length > 0) {
    parsed._unlock_schedule_source = hasRealUnlock ? 'surf' : 'ai_inference';
  }

  // ── Q36-B：recommendation 入场价反幻觉检测
  // Hyperliquid 案例（2026-06-24）：fair_value=$59.4B + recommendation 建议
  // 回调至 $500M-1B 建仓 = 99% 折扣，自相矛盾，且 FDV/Rev 80.9x 倍率无 ground truth 支撑。
  // 检测逻辑：从 recommendation 提取美元数字，若 < fair_value × 0.3 且 prompt 没明确依据，
  // 标记 _entry_price_suspicious 给 UI 显示警告。不强制清空（不像 unlock_schedule 那样
  // 可以直接删，recommendation 删了 UI 会更糟），让用户带 caveat 自己判断。
  const recForCheck = String(parsed.recommendation || '');
  const fairValue = Number(parsed.fair_value_usd) || 0;
  if (recForCheck && fairValue > 0) {
    // 提取所有 $ 数字（支持 $500M / $1B / $1.5 亿 等表达）
    const numbers = [];
    const re = /\$?\s*([\d.]+)\s*([MBKmbk万亿]|million|billion)?/g;
    let m;
    while ((m = re.exec(recForCheck)) !== null) {
      const val = parseFloat(m[1]);
      if (!Number.isFinite(val)) continue;
      const suffix = (m[2] || '').toLowerCase();
      let scale = 1;
      if (suffix === 'k') scale = 1e3;
      else if (suffix === 'm' || suffix === 'million' || suffix === '万') scale = (suffix === '万') ? 1e4 : 1e6;
      else if (suffix === 'b' || suffix === 'billion' || suffix === '亿') scale = (suffix === '亿') ? 1e8 : 1e9;
      numbers.push(val * scale);
    }
    if (numbers.length > 0) {
      const minMentioned = Math.min(...numbers);
      const ratio = minMentioned / fairValue;
      // 触发条件：建议入场价下限 < fair_value × 0.3（70%+ 折扣）
      if (ratio < 0.3) {
        // 关键证据词检查：如果 recommendation 明确说了「赛道历史中位数估算」/
        // 「实测 TVL」/「需进一步验证」等 hedge 用语，认为 AI 有自觉，不标可疑
        const hasHedge = /历史中位数|实测|进一步验证|赛道平均|参考|无 ?Rev|无 ?数据|数据缺失|approximate|estimated|requires further/i.test(recForCheck);
        if (!hasHedge) {
          parsed._entry_price_suspicious = true;
          parsed._entry_price_ratio = Number(ratio.toFixed(3));
          console.warn(`[ai-score-shape] Q36 入场价可疑：fair_value=$${(fairValue/1e9).toFixed(1)}B, 建议下限=$${(minMentioned/1e9).toFixed(2)}B, 比率=${(ratio*100).toFixed(1)}% (项目=${details?.project_name || '?'})`);
        }
      }
    }
  }

  // ── 诊断 + 兜底：AI 偶发在长 prompt + GROUND TRUTH 压力下漏写
  // risks/strengths/catalysts，导致 UI 卡空白。先 log 出来便于追踪，
  // 然后注入占位文案保证 UI 不空。
  const arrayDiag = {
    risks: Array.isArray(parsed.risks) ? parsed.risks.length : 'missing',
    strengths: Array.isArray(parsed.strengths) ? parsed.strengths.length : 'missing',
    catalysts: Array.isArray(parsed.catalysts) ? parsed.catalysts.length : 'missing',
    risks_en: Array.isArray(parsed.risks_en) ? parsed.risks_en.length : 'missing',
    strengths_en: Array.isArray(parsed.strengths_en) ? parsed.strengths_en.length : 'missing',
    catalysts_en: Array.isArray(parsed.catalysts_en) ? parsed.catalysts_en.length : 'missing',
  };
  const missing = Object.entries(arrayDiag).filter(([, v]) => v === 'missing' || v === 0).map(([k]) => k);
  if (missing.length > 0) {
    console.warn(`[ai-score-shape] AI 输出缺/空字段：${missing.join(', ')} | 全量长度=${JSON.stringify(arrayDiag)} | 项目=${details?.project_name || '?'}`);
  } else {
    console.log(`[ai-score-shape] OK ${JSON.stringify(arrayDiag)}`);
  }

  // 兜底占位 — 让 UI 不再空白。用户看到 "⚠️ AI 未生成" 知道要刷新或反馈。
  const placeholderZh = '⚠️ AI 本次未生成此项 — 请点 🔄 强刷重试，或反馈给我们';
  const placeholderEn = '⚠️ Not generated this run — click 🔄 to refresh or report to us';
  for (const k of ['risks', 'strengths', 'catalysts']) {
    if (!Array.isArray(parsed[k]) || parsed[k].length === 0) parsed[k] = [placeholderZh];
    const enK = `${k}_en`;
    if (!Array.isArray(parsed[enK]) || parsed[enK].length === 0) parsed[enK] = [placeholderEn];
  }

  // ── ZH/EN 长度对齐：DeepSeek 在双语对齐上常偷工（ZH=6 EN=3），按 index
  //    渲染的前端会让 EN 视图回退到 ZH 文案，造成"英文页夹中文"的混杂。
  //    以 ZH 长度为准：EN 短则补占位（不复用 ZH，避免中文跑去英文页），
  //    EN 长则裁剪到 ZH 长度（保持成对）。
  for (const k of ['risks', 'strengths', 'catalysts']) {
    const zh = parsed[k];
    const en = parsed[`${k}_en`];
    if (!Array.isArray(zh) || !Array.isArray(en) || zh.length === en.length) continue;
    if (zh.length > en.length) {
      parsed[`${k}_en`] = [...en, ...Array(zh.length - en.length).fill(placeholderEn)];
      console.warn(`[ai-score-shape] EN ${k} 短 (${en.length}<${zh.length})，已补占位对齐`);
    } else {
      parsed[`${k}_en`] = en.slice(0, zh.length);
      console.warn(`[ai-score-shape] EN ${k} 长 (${en.length}>${zh.length})，已截到 ZH 长度`);
    }
  }

  // ── Q57 标量字段 zh/en 配对兜底
  // DeepSeek 兜底输出经常漏 _en 字段（schema warning 高发源），按 trio 模式
  // 同步补占位：单边有 → 另一边给翻译提示；双边空 → 各给"未生成"占位。
  // 覆盖字段：summary / recommendation / moat / valuation_basis（高频空缺）。
  const SCALAR_PAIRS = ['summary', 'recommendation', 'moat', 'valuation_basis'];
  for (const f of SCALAR_PAIRS) {
    const zh = parsed[f];
    const en = parsed[`${f}_en`];
    const zhEmpty = typeof zh !== 'string' || !zh.trim();
    const enEmpty = typeof en !== 'string' || !en.trim();
    if (zhEmpty && enEmpty) {
      parsed[f] = '（AI 本次未生成此项）';
      parsed[`${f}_en`] = '(Not generated this run)';
    } else if (zhEmpty) {
      parsed[f] = '（中文翻译未生成）';
    } else if (enEmpty) {
      parsed[`${f}_en`] = '(EN translation pending)';
    }
  }
  // exit_paths_en 兜底：以 zh 长度对齐补占位（仿 trio 逻辑）
  if (Array.isArray(parsed.exit_paths) && parsed.exit_paths.length > 0) {
    if (!Array.isArray(parsed.exit_paths_en) || parsed.exit_paths_en.length === 0) {
      parsed.exit_paths_en = parsed.exit_paths.map(() => '(EN translation pending)');
    } else if (parsed.exit_paths_en.length < parsed.exit_paths.length) {
      const need = parsed.exit_paths.length - parsed.exit_paths_en.length;
      parsed.exit_paths_en = [...parsed.exit_paths_en, ...Array(need).fill('(EN translation pending)')];
    }
  }
  // notes_en 兜底：notes 存在但 notes_en 空 → 镜像 keys 给占位
  if (parsed.notes && typeof parsed.notes === 'object' && !Array.isArray(parsed.notes)) {
    if (!parsed.notes_en || typeof parsed.notes_en !== 'object' || Array.isArray(parsed.notes_en)) {
      parsed.notes_en = {};
      for (const k of Object.keys(parsed.notes)) parsed.notes_en[k] = '(EN translation pending)';
    }
  }

  // ── 阶段 1 影子模式：v2 schema 校验，只观察不阻断
  // 阶段 2 才在写边界强制 + 写 _schema_version: 2 字段
  // 日志格式便于在 Railway 里 grep "[schema-shadow]" 统计违规分布
  try {
    const projectLabel = details?.project_name || details?.name || '?';
    const shadowOutput = { ...parsed, news_signals };
    const { ok, violations } = validateAnalysisV2(shadowOutput);
    if (ok && violations.length === 0) {
      console.log(`[schema-shadow] OK project=${projectLabel}`);
    } else {
      const summary = summarizeViolations(violations);
      console.warn(`[schema-shadow] ${ok ? 'WARN' : 'FAIL'} project=${projectLabel} | ${summary}`);
    }
  } catch (e) {
    // 校验器自身崩了不能影响主流程
    console.warn(`[schema-shadow] validator threw: ${e?.message || e}`);
  }

  return {
    ...parsed,
    news_signals,
    composite,
    stage_key: stageKey,
    stage_label: stageConfig.label,
    stage_focus: stageConfig.focus,
    stage_weights: stageWeights,
    stage_signals: stageSignals,   // 判断依据，用于 UI 展示
    stage_signals_en: stageSignals_en,
  };
}
